scalar JSON

type Query {
  # Organisation
  organisation(
    id: ID!
    orderUsersBy: OrderBy
    order: Order
    useClientApi: Boolean
    includeUsers: Boolean
    isAr: Boolean
    includeDATemplates: Boolean
    includeFinancialConfigFields: Boolean
  ): Organisation!
  organisations(ids: [ID!]!, useClientApi: Boolean): [Organisation!]
  organisationsRepresentativeOf(filter_representative_of_org_id: ID!, introducerId: ID): [Organisation!]
  miOrganisations(filters: MiOrganisationInput): MiOrganisationResponse!
  lenderNames: [Organisation!]
  lenderNamesCp: [Organisation!]
  mortgageClubs: [Organisation!]
  providerNames: [Organisation!]
  clubNames: [Organisation!]

  # User
  user(id: ID!, useClientApi: Boolean, includeFinancialConfigFields: Boolean): User
  users(ids: [ID!], useClientApi: Boolean): [User!]
  currentUser: User
  caseOwners(organisationId: ID!, filterOut: CaseOwnerUserRoles): [User!]

  # Search
  search(searchFragment: String!, owning_org_id: String, show_client: Boolean, bookmark: Int): SearchResult
  caseSearch(searchFragment: String!, bookmark: Int): CaseSearchResult
  clientExistsWithEmailOrPhone(client_id: String!, phone_number: String, email_address: String): CheckClientResponse

  # Client
  clientList(
    client_details: Boolean
    show_protected: [String!]
    show_summary: Boolean
    client_ids: [String!]
    filter_organisation_id: String
    filter_email_address: String
    filter_client_ext_ids: [String!]
    filter_order_reverse: Boolean
    bookmark: String
    page_size: Int
  ): GetClientResponse
  client(id: ID!, version: Int, filters: MIDocumentSummaryFiltersInput): ClientVersion!
  clientVersion(id: ID!, version: Int!, show_history: Int): ClientVersion!
  clientProtectedFields(
    id: ID!
    protectedFields: [String!]
    date_of_birth: Boolean
    maiden_or_previous_name: Boolean
    accounts: Boolean
    sort_code: Boolean
    number: Boolean
    previous_names: Boolean
    previous_first_name: Boolean
    previous_surname: Boolean
  ): ClientVersion!
  clients(ids: [ID!], organisationId: ID): [ClientVersion!]!
  clientAddresses(id: ID!): [ClientAddress]!
  clientVerifications(id: ID!, show_pass: Boolean, useClientApi: Boolean): ClientVerification!
  clientOrganisationRelationship(client_id: String!, organisation_id_or_ext: String): ClientOrganisationRelationship!
  clientOrganisationRelationships(client_id: String!): [MarketingPreferences!]

  authenticationProviders: [AuthProvider]
  cpUpdates(case_id: ID!): ClientPortalChanges!

  # Property
  properties: [PropertyVersion!]
  propertiesCp(id: ID!): [PropertyVersion!]
  property(id: ID!, version: Int, show_history: Int): PropertyVersion!
  propertyCp(id: ID!): PropertyVersion!

  # Property Summary
  summary(id: ID!, include_calculated_values: Boolean, include_source_response: Boolean): CaseSummary!

  # Case
  case(
    id: ID!
    hydrate_related_cases: Boolean
    include_source_response: Boolean
    show_pass: Boolean
    show_documents: Boolean
    filters: MIDocumentSummaryFiltersInput
  ): CaseVersion!
  cases(ids: [ID!]): [CaseVersion!]!
  allCases(params: GetCaseRequestInput): CaseResponse!
  casesResponse(organisationId: ID!, params: GetCaseRequestInput): CaseResponse!
  clientCases(clientId: ID!, fromClientPortal: Boolean, includeClientNames: Boolean): [CaseVersion!]!
  directorClientCases(
    clientId: ID!
    fromClientPortal: Boolean
    includeClientNames: Boolean
    deniedAccess: Boolean
  ): [CaseVersion!]!

  allClientCases(
    clientId: ID!
    fromClientPortal: Boolean
    includeClientNames: Boolean
    deniedAccess: Boolean
    includeDeniedCases: Boolean
  ): [CaseVersion!]!

  caseClients(caseId: ID!): [ClientVersion!]!
  preferences(id: ID!): CasePreferences
  caseProperties(id: ID!): CaseVersion!
  caseSuitability(id: ID!): CaseVersion!
  complianceCases(organisationId: ID!, params: GetCaseRequestInput): CaseCompliance!
  caseVersionsTimeline(id: ID!): CaseVersionsTimelineResponse!
  caseVersion(id: ID!, version: Int!, show_history: Int): CaseVersion!

  # Note
  getFilteredNotes(params: GetFilteredNoteRequestInput, useClientApi: Boolean): GetFilteredNoteResponse!
  getNotes(params: GetNoteRequestInput, useClientApi: Boolean, hydrateCreator: Boolean): GetNoteResponse!
  getNote(params: GetNoteRequestInput, useClientApi: Boolean, hydrateCreator: Boolean): GetNoteResponse!

  # Mortgage
  mortgageVersion(id: ID!, version: Int!, show_history: Int): Mortgage
  mortgages: [Mortgage!]

  # DIP
  canDoDip(input: DipInput!): [Dip!]!
  getDipStatus(input: DipStatusInput!): SubmitDip
  applyForDip(id: ID!, mortgageId: ID!, reservation: Boolean): ApplyForDip
  canDoDipNoProperty(input: NoPropertyDipInput!): Dip!
  fmaFields(caseId: ID!, mortgageId: ID!): FMAFields!
  solicitorSearch(q: ID!): [SolicitorSearchResult!]

  # User and auth
  authUrls(email: String!): AuthURLs
  userExistsWithEmail(email: String!): UserExistsResponse
  kratosSettingsFlow(flow_id: String): KratosSettingsFlow!
  kratosLoginFlow: KratosLoginFlow!

  # Address
  addressLookup(searchFragment: String!, id: ID!): [AddressAndFieldUpdates!]!
  addressLookupSummary(searchFragment: String!): [AddressSummary!]!
  addressLookupList(searchFragment: String!, pathFilter: String!): [AddressSummary!]!
  addressLookupCP(searchFragment: String!, id: ID!): [AddressAndFieldUpdates!]!
  addressLookupSummaryCP(searchFragment: String!): [AddressSummary!]!
  addressLookupListCP(searchFragment: String!, pathFilter: String!): [AddressSummary!]!

  # DocumentEntity
  renderedDocument(
    template_name: TemplateType
    template_id: ID
    client_ids: [String!]
    mortgage_ids: [String!]
    organisation_ids: [String!]
    user_ids: [String!]
    case_ids: [String!]
    product_ids: [String!]
    store_document: Boolean!
    preview: Boolean
    template_style: TemplateStyleVariant
  ): DocumentEntity!
  renderedTestTemplate(
    template_name: TemplateType!
    template_code: String!
    organisation_ids: [String!]
    user_ids: [String!]
    client_ids: [String!]
    case_ids: [String!]
    mortgage_ids: [String!]
    product_ids: [String!]
    note_ids: [String!]
  ): DocumentEntity!

  documentDetails(docId: ID!, params: GetDocumentParams): DocumentEntity
  documentDetailsList(docIds: [ID!]!, documentTypes: [DocumentKind!], isCp: Boolean): [DocumentEntity!]!
  documentSummaries(filter_owning_client_id: String, filter_case_id: ID, filter_archived: Boolean): [DocumentEntity!]
  documentVerifications(
    documentId: ID!
    params: DocumentVerificationQueryParams
    includeUser: Boolean
  ): [DocumentVerification!]
  documentVerificationsCp(documentId: ID!, params: DocumentVerificationQueryParams): [DocumentVerificationCp!]
  documentRequirements(filter_organisation_ids: [ID!]!, requirement_details: Boolean): [DocumentRequirement!]
  brokersEula(brokerClientId: ID!): DocumentEntity

  miDocuments(filters: MIDocumentSummaryFiltersInput): MIDocumentSummaryResponse
  caseAndClientsMIDocumentsSummaries(case_id: String!, client_ids: [String!]!): MIDocumentSummaryResponse

  # Accounting
  getLedgers(input: GetLedgersInput!): GetLedgersResponse!
  getProviderStatements(input: GetProviderStatementsInput!): GetProviderStatementsResponse!
  getLedgersByCaseId(caseId: ID!): CaseLedgers!
  getLedgerFilters(input: GetLedgerFiltersInput): LedgerFilters!
  getProviderStatementFilters(input: GetProviderStatementFiltersInput): ProviderStatementFilters!
  getLedgerSummary(input: GetLedgerSummaryInput!): GetLedgerSummaryResponse!
  getGoCardlessAuthURL: GoCardlessURLResponse
  getGoCardlessAccountStatus: GoCardlessStatusResponse
  hasGoCardlessMandate(client_id: String!): GoCardlessStatusResponse

  # Permission
  permissionResolution(input: PermissionResolutionInput!): PermissionResolution
  permission(names: [String!], ids: [ID!], show_allowlist: Boolean): PermissionResponse
  permissionAssignments(userId: ID, groupId: ID): PermissionAssignmentResponse

  # Protection
  productEstimate(input: ProductEstimateInput!): ProductEstimateResponse!
  getSolutionSourcingResults(location: String!): GetSolutionSourcingResultsResponse!
  getSolutionSourcingResultsV2(location: String!): GetSolutionSourcingResultsResponse!
  protectionProducts(protectionProductDetails: Boolean, filterCaseIds: [ID!]): [ProtectionProduct!]
  protectionProductsForClients(protectionProductDetails: Boolean, filterClientIds: [ID!]!): [ProtectionProduct!]
  isProtectionAuthValid(input: ProtectionAuth!): Boolean
  isProtectionAuthValidV2(input: IressCredentialsPair!): Boolean
  quoteSource(input: SplitCoverBenefitsInput): [SplitCoverQuote!]
  quoteSourceV2(input: SplitCoverBenefitsInput): [SplitCoverQuote!]

  # General insurance
  generalInsuranceQuote(case_id: String!, quote_id: ID!): GetGeneralInsuranceQuoteResponse
  generalInsuranceQuotes(input: GeneralInsuranceInput): GetGeneralInsuranceQuotesResponse
  generalInsuranceProducts(
    input: GetGeneralInsuranceProductsInput
    useClientApi: Boolean
  ): GeneralInsuranceProductsResponse
  checkGeneralInsuranceQuotesExist(case_id: ID!, quote_ids: [ID!]!): [ID!]!
  isGeneralInsuranceAuthValid(input: GeneralInsuranceAuthCreds!): Boolean

  # Companies House
  companyAutocomplete(input: String!): CHAutocompleteResponse
  CHAutocompleteResponse(q: String!): CHAutocompleteResponse
  getCompanyDetail(companyId: String!): CHCompany
  CHCompany(companyId: String!): CHCompany
  getDirectorsFromCHCompany(company: CHCompanyInput!, organisation_id: String!): DirectorsFromCHResult

  # Reports
  reportsCurrentPipeline(body: ReportsCaseStatsFilterInput!): ReportsCurrentPipelineResponse!
  reportsCancellationReason(body: ReportsCaseStatsFilterInput!): ReportsCancellationReasonResponse!
  reportsCaseWaterfall(body: ReportsCaseStatsFilterInput!): ReportsCaseWaterfallResponse!
  reportsCaseWaterfallIntroducer(body: ReportsIntroducerCaseStatsFilterInput!): ReportsCaseWaterfallIntroducerResponse!
  reportsRevenue(body: ReportsRevenueInput!): ReportsRevenueResponse!
  reportsReportQueue: ReportsReportQueueResponse!
  reportsCommissions(body: ReportsCommissionsInput!): ReportsCommissionsResponse!
  reportsPredictedRevenue(body: ReportsCaseStatsFilterInput!): ReportsCurrentPipelineResponse!
  reportsDiscovery: ReportsDiscoveryResponse!
  reportsFilterOptions(body: ReportsFilterInput!): ReportsFilterOptionsResponse!
  consumerDutyResults(body: SurveyResultsInput): SurveyResultsResponse!

  redirectToLogin: Boolean

  ### Client Portal
  # Client User details
  clientUser: ClientVersion
  caseUser(id: ID!, fromClientPortal: Boolean): CaseVersion!
  caseUserSurvey(id: ID!): CaseVersion!
  clientsUser(ids: [ID!], organisationId: ID): [ClientVersion!]!
  clientsUserDetails(ids: [ID!], organisationId: ID): [ClientVersion!]!
  clientUserProtectedFields(
    id: ID!
    protectedFields: [String!]
    date_of_birth: Boolean
    maiden_or_previous_name: Boolean
    accounts: Boolean
    sort_code: Boolean
    number: Boolean
    previous_names: Boolean
    previous_first_name: Boolean
    previous_surname: Boolean
  ): ClientVersion

  dashboardDocumentsCp(id: ID!, entity: String, filters: MIDocumentSummaryFiltersInput): DashboardDocumentsCpResponse!

  caseVerificationsCp(id: ID!, show_pass: Boolean, show_documents: Boolean): CaseVerification!

  clientUserIddDocuments(
    filter_owning_client_id: String
    filter_case_id: ID
    filter_archived: Boolean
  ): [DocumentEntity!]!

  # Client User documents
  clientUserMiDocumentsSummaries(
    id: String!
    entity: String
    filters: MIDocumentSummaryFiltersInput
  ): MIDocumentSummaryResponse
  clientUserDocumentDetails(docId: ID!, params: GetDocumentParams): DocumentEntity

  # CommissionStructure
  getCommissionStructures(
    input: CommissionStructureRequestInput
    includePayingOrg: Boolean
  ): CommissionStructureResponse!
  introducers(input: IntroducerCommissionsRequestInput!, includeUsers: Boolean): IntroducerCommissions!

  # Valuation
  getValuation(acrePropertyID: Int!): Valuation

  # Roles
  getRolesByName(roleNames: [String!]!): [Role!]

  # Group
  getGroupsBy(input: GetGroupInput): [Group!]
  getGroupsForOrg(filter_organisation_id: String, show_disabled: Boolean, fetchUsers: Boolean): [Group!]
  getGroups(ids: [ID!]!, fetchUsers: Boolean): [Group!]

  # Dashboard Case List
  getDashboardCaseList(input: GetMiCaseListFilterInput!): GetMiCaseListResponse!

  getCaseReferralList(input: GetCaseReferralListFilterInput!): GetCaseReferralListResponse!

  # Notifications
  notifications(userId: ID!): [NotificationEvent!]
  getNotificationsToken: NotificationToken
  getWebPushPublicKey: WebPushPublicKey
  getWebPushSubscriptions: [WebPushSubscription!]

  # Oauth
  getOauthCheck: OauthCheckResponse
  getOauthCheckForUsers(ids: [ID!]): OauthUserCheckResponse
  getOauthRedirectLink(input: OauthRedirectInput!): String

  # Calendar
  getFreeBusyTimes(start: Int!, end: Int!, emails: [String!]!): [NylasFreeBusyObject!]!
  referredCaseList(input: GetReferredCaseListFilterInput!): GetMiCaseListResponse!

  # Referral Partnerships
  getReferralPartners(input: GetReferralPartnersInput!): GetReferralPartnersResponse!

  # Firm management
  getFirmList(filter: MiOrganisationsInput): MiOrganisationsResponse!

  # Scheduler
  getSchedulerAvailability(startTime: Float!, endTime: Float!, key: String!): SchedulerAvailabilitySlots
  getSchedulerEventInfo(key: String!): SchedulerEventInfo
  getSchedulerEventInfoDirect(caseId: String!): SchedulerEventInfo
  getSchedulerEventsDirect(caseId: String!): [SchedulerEvent!]!
  getIsDirectSchedulingAllowed(caseId: String!): Boolean!

  getSchedulerAvailabilityDirect(startTime: Float!, endTime: Float!, caseId: String!): SchedulerAvailabilitySlots

  getMortgageProductCodes(isBtl: Boolean!, productCode: String, lender: String): [MortgageProductCode!]
  getMortgageProductBySourcingProductId(input: MortgageProductBySourcingIdInput!): MortgageProduct

  # KBA
  kbaQuestionnaire(userId: ID!, useClientApi: Boolean): KbaAttempt

  loginAsSupportUser(id: ID!): SupportUser

  # Credit Report
  creditReport(useClientApi: Boolean, clientId: String, showSearches: Boolean, documentId: String, requests: [CreditReportRequestInput!]): CreditReportResponse
  creditRefreshAvailability(clientId: ID!): CreditRefreshResult

  # Tags
  tagsDiscovery: [Tag!]

  # Filters
  dashboardsFiltersDiscovery: DashboardsFilter
  orgAndIntroducerAndAdvisorFiltersDiscovery(input: GetMiCaseListFilterInput): OrgAndIntroducerAndAdvisorFilter
  orgRegulatingFiltersDiscovery: OrgRegulatingFilter

  getPayments(useClientApi: Boolean, includeOrganisation: Boolean): GetPaymentsResponse
  getNeedsMandate: GetNeedsMandateResponse

  survey(id: ID!): Survey

  # LBG Shared Ownership Companies
  lbgSharedOwnershipCompanies: [LBGSharedOwnershipCompanyOption!]
  isLbgAuthValid(input: LbgAuthCreds!): Boolean

  # Vulnerability
  vulnerabilityTemplates(provider: ID!): VulnerabilityTemplateResponse
  vulnerabilityReport(assessment_uuid: ID!, client: ID!): VulnerabilityReport
  clientGetVulnerabilityQuestion(assessment_uuid: ID!, case: ID!): ClientVulnerabilityQuestionResponse
}
